import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Button from "@mui/material/Button";
import { apiErrorMessage } from "../../../utils/apiError";

const MIN_REASON = 10;

/**
 * AdTransferDialog - move Platform Manager from one account to another.
 *
 * AD is a singleton: it cannot be assigned from the role picker and cannot be
 * revoked, because a platform with no AD cannot be recovered from the console.
 * Transfer is the only legal move, and it is deliberately a two-step dialog
 * with a mandatory reason rather than a menu entry, because the admin
 * performing it may be signing away their own access.
 */
function AdTransferDialog({ open, fromUser, callerId, onClose, onTransferred }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  function reset() {
    setSearch("");
    setResults([]);
    setTarget(null);
    setReason("");
    setError(null);
  }

  function close() {
    if (busy) return;
    reset();
    onClose();
  }

  // The same users search the sponsor picker uses, WITHOUT its BDC filter:
  // Platform Manager can be handed to any admin account, not only to a BDC.
  function runSearch() {
    const token = localStorage.getItem("accessToken");
    setSearching(true);
    axios
      .post(
        process.env.REACT_APP_API + "users",
        { page: 1, limit: 50, searchTerm: search },
        { headers: { "x-access-token": token } }
      )
      .then((response) => {
        const rows = response?.data?.data?.rows || [];
        setResults(rows.filter((u) => Number(u.id) !== Number(fromUser?.id)));
      })
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }

  const trimmedReason = reason.trim();
  const canSubmit = !!target && trimmedReason.length >= MIN_REASON && !busy;

  async function submit() {
    setBusy(true);
    setError(null);
    const token = localStorage.getItem("accessToken");
    try {
      await axios.post(
        process.env.REACT_APP_API + `admin/users/${target.id}/roles/transfer-ad`,
        { reason: trimmedReason },
        { headers: { "x-access-token": token } }
      );
      // Signing away your own AD invalidates the grants this session was
      // rendered with, and every AD-only control on screen is now a lie.
      const selfTransfer = Number(fromUser?.id) === Number(callerId);
      reset();
      onTransferred({ selfTransfer, targetName: userLabel(target) });
    } catch (err) {
      setError(apiErrorMessage(err, "Transfer failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} aria-labelledby="ad-transfer-title" maxWidth="sm" fullWidth>
      <DialogTitle id="ad-transfer-title">Transfer Platform Manager</DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 0, fontSize: 13, color: "#4b5563" }}>
          Platform Manager moves from {userLabel(fromUser)} to the account you
          choose. There is exactly one at a time, and the platform cannot be
          left without one.
        </p>

        <div className="field">
          <label className="field__label" htmlFor="ad-transfer-search">
            Find the new Platform Manager
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="ad-transfer-search"
              className="field__input"
              value={search}
              disabled={busy}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or email"
            />
            <Button onClick={runSearch} disabled={busy || searching}>
              {searching ? "Searching..." : "Search"}
            </Button>
          </div>
        </div>

        <div style={{ maxHeight: 200, overflow: "auto", margin: "8px 0" }}>
          {results.length === 0 ? (
            <span className="field__hint">No candidates listed yet.</span>
          ) : (
            results.map((u) => (
              <label
                key={u.id}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}
              >
                <input
                  type="radio"
                  name="ad-transfer-target"
                  checked={target?.id === u.id}
                  onChange={() => setTarget(u)}
                  disabled={busy}
                />
                <span>{userLabel(u)}</span>
              </label>
            ))
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ad-transfer-reason">
            Reason (mandatory, min {MIN_REASON} chars)
          </label>
          <textarea
            id="ad-transfer-reason"
            className="field__textarea"
            rows={3}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {error && <p style={{ color: "#b91c1c", fontSize: 13 }}>{error}</p>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {busy ? "Transferring..." : "Transfer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function userLabel(u) {
  if (!u) return "this account";
  const full = `${u.firstname || ""} ${u.lastname || ""}`.trim();
  return full ? `${full} (${u.email || `#${u.id}`})` : u.email || `#${u.id}`;
}

export default AdTransferDialog;
